import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, ArrowUpRight, Calendar, Check, Clock, MapPin, Users } from 'lucide-react'
import PageHero from '../components/PageHero'
import SectionNav from '../components/SectionNav'
import { divisions, equipment, passes, rule25 } from '../data/content'

const events = [
  {
    name: 'Clases de golf adaptado',
    date: 'Martes y jueves',
    place: 'Deportivo Xochimilco · 9:30–13:00',
    status: 'Cada semana',
    description: 'Principiantes y avanzados. Discapacidad motriz, sensorial e intelectual. No hace falta equipo propio.',
    href: '/contact',
  },
  {
    name: 'I Campeonato Nacional FMG Adaptive Open',
    date: '2026',
    place: 'Club de Golf México',
    status: 'Nacional',
    description: 'Stableford a 18 hoyos diarios. 25 jugadores de México, Guatemala, Argentina y Colombia, con discapacidad motriz, intelectual y sensorial.',
    href: '/blog/mexico-2012-2026',
  },
  {
    name: 'G4D Tour',
    date: 'Desde 2022',
    place: 'Sedes del DP World Tour',
    status: 'Circuito',
    description: 'Siete torneos en los mismos campos y las mismas semanas que el circuito europeo: The Belfry, Wentworth, Valderrama, Dubái.',
    href: '/history',
  },
  {
    name: 'Expogolf México 2026',
    date: '5 y 6 de septiembre',
    place: 'Centro Citibanamex, CDMX',
    status: 'Industria',
    description: 'La asociación usa el encuentro de la industria para visibilidad, alianzas y comunidad.',
    href: 'https://expogolfmexico.com.mx/services/',
  },
]

const benefits = [
  'Clases regulares en Xochimilco',
  'Instructores que adaptan el swing a cada cuerpo',
  'Salidas a campo para quien ya juega',
  'Camino hacia torneos nacionales e internacionales',
  'Red en ocho estados, no solo en la capital',
  'Comunidad de juego, no solo de rehabilitación',
]

const Golf = () => {
  const [activeRule, setActiveRule] = useState(rule25[0].id)
  const rule = rule25.find((item) => item.id === activeRule)

  return (
    <div className="min-h-screen bg-ivory">
      <PageHero
        eyebrow="Reglas · Equipo · Clases"
        title="Jugar en las"
        highlight="mismas reglas"
        description="La Regla 25 no regala ventaja. Iguala el campo para quien ve poco, juega sentado, usa prótesis o necesita un ayudante."
      />

      <SectionNav
        items={[
          { href: '#agenda', label: 'Agenda' },
          { href: '#clases', label: 'Clases' },
          { href: '#regla-25', label: 'Regla 25' },
          { href: '#equipo', label: 'Equipo' },
          { href: '#ranking', label: 'Ranking' },
        ]}
      />

      <section id="agenda" className="scroll-mt-36 section-pad mx-auto max-w-7xl">
        <div className="max-w-2xl">
          <p className="eyebrow">Qué está en juego</p>
          <h2 className="display mt-4 text-4xl md:text-5xl">De la clase del martes al circuito mundial.</h2>
        </div>
        <div className="mt-12 grid gap-5 md:grid-cols-2">
          {events.map((event) => {
            const internal = event.href.startsWith('/')
            const Tag = internal ? Link : 'a'
            const props = internal
              ? { to: event.href }
              : { href: event.href, target: '_blank', rel: 'noopener noreferrer' }
            return (
              <Tag
                key={event.name}
                {...props}
                className="group flex h-full flex-col rounded-3xl bg-white p-7 ring-1 ring-leaf/10 transition-shadow hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-4">
                  <span className="rounded-full bg-cream px-3 py-1 text-xs font-semibold uppercase tracking-wide text-leaf">
                    {event.status}
                  </span>
                  <ArrowUpRight className="h-5 w-5 text-leaf transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </div>
                <h3 className="mt-5 font-serif text-2xl leading-snug">{event.name}</h3>
                <p className="mt-2 text-sm font-medium text-leaf">
                  {event.date}
                  <span className="text-mist"> · {event.place}</span>
                </p>
                <p className="mt-4 flex-1 leading-relaxed text-mist">{event.description}</p>
              </Tag>
            )
          })}
        </div>
      </section>

      <section id="clases" className="scroll-mt-36 bg-cream">
        <div className="section-pad mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
          <div>
            <p className="eyebrow">Xochimilco</p>
            <h2 className="display mt-4 text-4xl md:text-5xl">Dos mañanas a la semana. El equipo lo ponemos nosotros.</h2>
            <p className="mt-5 text-lg leading-relaxed text-mist">
              El golf aquí es integración: cuida el cuerpo, la cabeza y el vínculo con la familia. No hace falta haber jugado antes.
            </p>
            <Link to="/contact" className="btn-primary mt-8">
              Quiero una clase
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              { icon: Calendar, label: 'Martes y jueves' },
              { icon: Clock, label: '9:30 a 13:00' },
              { icon: MapPin, label: 'Deportivo Xochimilco' },
              { icon: Users, label: 'Principiantes y avanzados' },
            ].map((item) => (
              <div key={item.label} className="rounded-3xl bg-white p-6 ring-1 ring-leaf/10">
                <item.icon className="h-5 w-5 text-leaf" aria-hidden="true" />
                <p className="mt-4 font-serif text-2xl leading-snug">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="regla-25" className="scroll-mt-36 section-pad mx-auto max-w-7xl">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="eyebrow">Desde enero de 2023</p>
            <h2 className="display mt-4 text-4xl">La Regla 25</h2>
            <p className="mt-4 leading-relaxed text-mist">
              The R&A y la USGA integraron las adaptaciones al libro oficial. Aplican en stroke play y match play, y tocan cerca de veinte puntos del reglamento general.
            </p>
            <div className="mt-8 flex flex-col gap-2" role="tablist" aria-label="Apartados de la Regla 25">
              {rule25.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  id={`tab-${item.id}`}
                  aria-selected={activeRule === item.id}
                  aria-controls={`panel-${item.id}`}
                  onClick={() => setActiveRule(item.id)}
                  className={`rounded-2xl px-4 py-3 text-left transition-colors ${
                    activeRule === item.id ? 'bg-leaf text-white' : 'bg-cream text-ink hover:bg-white'
                  }`}
                >
                  <span className="text-xs font-semibold uppercase tracking-eyebrow opacity-80">Regla {item.code}</span>
                  <span className="mt-1 block font-serif text-xl">{item.title}</span>
                </button>
              ))}
            </div>
          </div>

          <div
            role="tabpanel"
            id={`panel-${rule.id}`}
            aria-labelledby={`tab-${rule.id}`}
            className="rounded-[2rem] bg-fairway p-8 text-cream md:p-12 lg:col-span-8"
          >
            <p className="eyebrow text-sun">{rule.permit}</p>
            <h3 className="mt-4 font-serif text-4xl">{rule.title}</h3>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-cream/80">{rule.summary}</p>
            <ul className="mt-8 space-y-4">
              {rule.points.map((point) => (
                <li key={point} className="flex gap-3 leading-relaxed text-cream/90">
                  <Check className="mt-1 h-5 w-5 shrink-0 text-sun" aria-hidden="true" />
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section id="equipo" className="scroll-mt-36 bg-cream">
        <div className="section-pad mx-auto max-w-7xl">
          <div className="max-w-2xl">
            <p className="eyebrow">Ingeniería</p>
            <h2 className="display mt-4 text-4xl md:text-5xl">Cuatro piezas que abren el campo.</h2>
            <p className="mt-4 text-lg text-mist">
              El equipo busca devolver el swing y, al mismo tiempo, no dañar el césped. Por eso un monoplaza puede entrar al green.
            </p>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            {equipment.map((item, index) => (
              <article key={item.name} className="rounded-3xl bg-white p-8 ring-1 ring-leaf/10">
                <p className="font-serif text-sm tracking-eyebrow text-leaf">0{index + 1}</p>
                <h3 className="mt-4 font-serif text-3xl">{item.name}</h3>
                <p className="mt-1 text-sm font-medium text-mist">{item.maker}</p>
                <p className="mt-4 leading-relaxed text-mist">{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="ranking" className="scroll-mt-36 section-pad mx-auto max-w-7xl">
        <div className="max-w-2xl">
          <p className="eyebrow">EDGA · WR4GD</p>
          <h2 className="display mt-4 text-4xl md:text-5xl">Tres pases. Tres formas de competir.</h2>
          <p className="mt-4 text-lg leading-relaxed text-mist">
            La clasificación médica usa las categorías del Comité Paralímpico Internacional. El ranking suma 104 semanas y lo administran EDGA, The R&A y la USGA.
          </p>
        </div>

        <div className="mt-12 grid gap-5 lg:grid-cols-3">
          {passes.map((pass) => (
            <article key={pass.name} className="flex h-full flex-col rounded-3xl bg-white p-7 ring-1 ring-leaf/10">
              <h3 className="font-serif text-2xl">{pass.name}</h3>
              <dl className="mt-6 space-y-4 text-sm leading-relaxed">
                <div>
                  <dt className="font-semibold text-leaf">Elegibilidad</dt>
                  <dd className="mt-1 text-mist">{pass.eligible}</dd>
                </div>
                <div>
                  <dt className="font-semibold text-leaf">Dónde juega</dt>
                  <dd className="mt-1 text-mist">{pass.events}</dd>
                </div>
                <div>
                  <dt className="font-semibold text-leaf">Ranking</dt>
                  <dd className="mt-1 text-mist">{pass.ranking}</dd>
                </div>
              </dl>
            </article>
          ))}
        </div>

        <div className="mt-5 grid gap-5 md:grid-cols-3">
          {divisions.map((division) => (
            <article key={division.name} className="rounded-3xl bg-fairway p-7 text-cream">
              <p className="text-sm font-semibold uppercase tracking-eyebrow text-sun">{division.detail}</p>
              <h3 className="mt-3 font-serif text-2xl">{division.name}</h3>
              <p className="mt-3 text-cream/75">{division.text}</p>
            </article>
          ))}
        </div>

        <p className="mt-8 text-sm text-mist">
          Categorías del IPC que revisa la EDGA: deficiencia articular, fuerza muscular, longitud de extremidades, amputación, hipertonía, ataxia, atetosis, baja estatura, discapacidad visual e intelectual.
        </p>
      </section>

      <section className="bg-leaf text-cream">
        <div className="section-pad mx-auto grid max-w-7xl gap-12 lg:grid-cols-2">
          <div>
            <p className="eyebrow text-sun">Afiliación</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl">Una credencial para entrar a la comunidad.</h2>
            <p className="mt-5 text-cream/80 leading-relaxed">
              El registro es para quien juega con discapacidad y quiere clases, salidas y un camino hacia la competencia.
            </p>
            <Link to="/contact" className="btn-brass mt-8">
              Pedir informes
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <ul className="grid content-center gap-3 sm:grid-cols-2">
            {benefits.map((benefit) => (
              <li key={benefit} className="flex gap-3 rounded-2xl bg-white/10 px-4 py-4">
                <Check className="mt-0.5 h-5 w-5 shrink-0 text-sun" aria-hidden="true" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  )
}

export default Golf
